import { useState } from 'react'
import { Dropdown, Switch } from 'antd'
import type { MenuProps } from 'antd'
import { LogoutOutlined, CustomerServiceOutlined, CommentOutlined, MailOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import { LANGUAGE_STORAGE_KEY } from '@/App'
import { useAuthStore } from '@/store/auth-store'
import { useAuthService, useCompanyService } from '@/api/use-service'
import { ContactModal, ContactType } from '@/components/contact-sales-modal'

interface Props {
  className?: string
}

export const Avatar = ({ className }: Props) => {
  const { user, logout: storeLogout } = useAuthStore()
  const { logout } = useAuthService()
  const { updateCompany } = useCompanyService()
  const [contactModalOpen, setContactModalOpen] = useState(false)
  const [contactType, setContactType] = useState<ContactType>('support')
  const { t, i18n } = useTranslation()

  const onLogout = async () => {
    const { error } = await logout.invoke()
    if (!error) {
      storeLogout()
    }
  }

  const openContactModal = (type: ContactType) => {
    setContactType(type)
    setContactModalOpen(true)
  }

  const handleLanguageChange = async (lang: 'en' | 'es') => {
    if (lang === currentLang) return
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang)
    const companyId = user?.companyIds?.[0]
    if (companyId) {
      await updateCompany.invoke({ pathParam: companyId, payload: { language: lang } }).catch(() => {
        // Best-effort: localStorage is the primary source, API persists for cross-device consistency
      })
    }
    window.location.reload()
  }

  const currentLang = i18n.language

  const firstLetter = user?.name?.[0] ?? user?.email?.[0] ?? ''

  const items: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <div className="px-1 py-2">
          <div className="font-semibold text-text-primary">{user?.name || 'User'}</div>
          <div className="text-xs text-gray-500">{user?.email}</div>
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'support',
      label: t('avatar_menu.support'),
      icon: <CustomerServiceOutlined />,
      onClick: () => openContactModal('support'),
    },
    {
      key: 'feedback',
      label: t('avatar_menu.feedback'),
      icon: <CommentOutlined />,
      onClick: () => openContactModal('feedback'),
    },
    {
      key: 'contact',
      label: t('avatar_menu.contact'),
      icon: <MailOutlined />,
      onClick: () => openContactModal('contact'),
    },
    {
      key: 'language',
      label: (
        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          <span className={currentLang === 'en' ? 'font-semibold text-brand' : 'text-gray-400'}>EN</span>
          <Switch
            checked={currentLang === 'es'}
            onChange={checked => handleLanguageChange(checked ? 'es' : 'en')}
            size="small"
            className="bg-brand"
          />
          <span className={currentLang === 'es' ? 'font-semibold text-brand' : 'text-gray-400'}>ES</span>
        </div>
      ),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: t('sidebar.logout'),
      icon: <LogoutOutlined />,
      danger: true,
      onClick: onLogout,
    },
  ]

  return (
    <>
      <Dropdown menu={{ items }} placement="bottomRight" trigger={['click']}>
        <a
          href="#"
          onClick={e => e.preventDefault()}
          className={`${className} avatar flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-full bg-brand text-[20px] font-medium leading-[20px] text-white hover:text-white`}
        >
          {firstLetter ?? ''}
        </a>
      </Dropdown>

      <ContactModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        type={contactType}
        user={user ? { email: user.email, name: user.name } : undefined}
      />
    </>
  )
}
